// MfaEnforcementGate — mandatory authenticator (TOTP) setup for any signed-in
// user who has no TOTP activated. The Cognito pool runs MFA=OPTIONAL (required
// to allow passkeys as a first factor); this gate guarantees every user still
// holds TOTP, so a password sign-in always carries a second factor and no login
// is ever single-factor. Passkeys are additive on top of this.
//
// It blocks the app (full-screen) until setup completes; the only ways out are
// finishing setup or signing out.
import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { useFileContext } from '../../ContextAPI/FileContext';
import { buildOtpauthUri } from '../../utils/otpauth';
import './MfaEnforcementGate.css';

// DISABLED: the shared Cognito pool runs MfaConfiguration=ON, so Cognito itself
// requires TOTP on every sign-in (and issues CONTINUE_SIGN_IN_WITH_TOTP_SETUP to
// users who don't have it yet — handled in LoginModal). This app-level gate was
// only needed while the dev pool ran MFA=OPTIONAL to permit passkeys. Keeping it
// on would add no enforcement and risks false-positively blocking real users, as
// it did once already. Code retained in case MFA=OPTIONAL is ever needed again.
const MFA_GATE_ENABLED = false;

const MfaEnforcementGate = () => {
  const { auth, userEmail, logout } = useFileContext();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [starting, setStarting] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The login flow is the source of truth: LoginModal sets `mfaSetupRequired`
  // when a PASSWORD sign-in completes WITHOUT an MFA challenge (i.e. the account
  // has no TOTP), and clears it whenever a TOTP challenge is passed. Reading this
  // flag avoids the false positives we hit querying fetchMFAPreference right
  // after sign-in (it can report "no TOTP" for a user who just passed MFA).
  useEffect(() => {
    if (!MFA_GATE_ENABLED || !auth) { setNeedsSetup(false); return; }
    let required = false;
    try { required = sessionStorage.getItem('mfaSetupRequired') === '1'; } catch (e) { /* ignore */ }
    setNeedsSetup(required);
  }, [auth]);

  // Begin TOTP setup (fetch a fresh secret + render the QR) once we know it's needed.
  const beginSetup = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const totp = await setUpTOTP();
      const sharedSecret = totp.sharedSecret;
      setSecret(sharedSecret);
      const uri = buildOtpauthUri({
        secret: sharedSecret,
        email: userEmail || 'account',
        issuer: 'RDV Systems',
      });
      const dataUrl = await QRCode.toDataURL(uri, { width: 200, margin: 2 });
      setQr(dataUrl);
    } catch (e) {
      console.warn('setUpTOTP failed:', e?.message || e);
      setError('Could not start authenticator setup. Please try again.');
    } finally {
      setStarting(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (needsSetup && !secret && !starting) beginSetup();
  }, [needsSetup, secret, starting, beginSetup]);

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator.'); return; }
    setBusy(true);
    setError('');
    try {
      await verifyTOTPSetup({ code });
      // Explicitly activate TOTP as the preferred MFA (don't rely on auto-activation).
      await updateMFAPreference({ totp: 'PREFERRED' });
      try { sessionStorage.removeItem('mfaSetupRequired'); } catch (e) { /* ignore */ }
      setNeedsSetup(false); // unlock the app
    } catch (e) {
      console.warn('verifyTOTPSetup failed:', e?.name || e?.message || e);
      setError('That code was incorrect or expired. Enter the current 6-digit code.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (!auth || !needsSetup) return null;

  return (
    <div className="mfa-gate__backdrop">
      <div className="mfa-gate" role="dialog" aria-modal="true" aria-label="Set up two-factor authentication">
        <h2 className="mfa-gate__title">Secure your account</h2>
        <p className="mfa-gate__subtitle">
          Two-factor authentication is required. Scan this with an authenticator app
          (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.
        </p>

        <div className="mfa-gate__qr">
          {qr ? (
            <img src={qr} alt="Authenticator QR code" />
          ) : (
            <div className="mfa-gate__qr-placeholder">{starting ? 'Generating…' : ' '}</div>
          )}
        </div>

        {secret && (
          <p className="mfa-gate__secret">
            Can’t scan? Enter this key:<br />
            <code>{secret}</code>
          </p>
        )}

        <input
          className="mfa-gate__code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) handleVerify(); }}
          autoFocus
        />

        {error && <p className="mfa-gate__error">{error}</p>}

        <button
          type="button"
          className="mfa-gate__verify"
          onClick={handleVerify}
          disabled={busy || code.length !== 6 || !secret}
        >
          {busy ? 'Verifying…' : 'Verify & continue'}
        </button>

        <button type="button" className="mfa-gate__signout" onClick={logout}>
          Sign out instead
        </button>
      </div>
    </div>
  );
};

export default MfaEnforcementGate;
